const ERROR_LOG_KEY = 'bl312-error-log';
const MAX_RECORDS = 20;
const MAX_DETAILS_LENGTH = 2000;

const CHUNK_ERROR_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|chunkloaderror|loading chunk [\w-]+ failed/i;

export interface DiagnosticRecord {
  at: string;
  context: string;
  name: string;
  message: string;
  details?: string;
  stack?: string;
  url: string;
  looksLikeChunkError: boolean;
  hasAuthCode: boolean;
  hasAuthError: boolean;
  hasStoredToken: boolean;
}

function safeStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  try {
    return JSON.stringify(value, (_key, val) => {
      if (val instanceof Error) {
        return { name: val.name, message: val.message, stack: val.stack };
      }
      if (typeof val === 'bigint') {
        return val.toString();
      }
      if (typeof val === 'function') {
        return `[Function ${val.name || 'anonymous'}]`;
      }
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) {
          return '[Circular]';
        }
        seen.add(val);
      }
      return val;
    });
  } catch {
    return String(value);
  }
}

function errorName(error: unknown): string {
  if (error instanceof Error) {
    return error.name;
  }
  if (error && typeof error === 'object') {
    const constructorName = (error as { constructor?: { name?: string } }).constructor?.name;
    const type = (error as { type?: unknown }).type;
    if (typeof type === 'string' && type) {
      return constructorName && constructorName !== 'Object' ? `${constructorName}:${type}` : type;
    }
    return constructorName ?? 'Object';
  }
  return typeof error;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    if (typeof record['message'] === 'string' && record['message']) {
      return record['message'];
    }
    for (const key of ['reason', 'error', 'params'] as const) {
      const nested = record[key];
      if (nested && nested !== error) {
        const nestedMessage = errorMessage(nested);
        if (nestedMessage && nestedMessage !== '[object Object]') {
          return nestedMessage;
        }
      }
    }
    if (typeof record['type'] === 'string' && record['type']) {
      return record['type'];
    }
    const serialized = safeStringify(error);
    if (serialized && serialized !== '{}') {
      return serialized;
    }
  }
  return String(error);
}

function errorStack(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.stack;
  }
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    if (typeof record['stack'] === 'string') {
      return record['stack'];
    }
    if (record['reason'] instanceof Error) {
      return record['reason'].stack;
    }
  }
  return undefined;
}

function errorDetails(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || error instanceof Error) {
    return undefined;
  }
  const serialized = safeStringify(error);
  if (serialized === '{}' || serialized === '[]') {
    return undefined;
  }
  return serialized.length > MAX_DETAILS_LENGTH
    ? `${serialized.slice(0, MAX_DETAILS_LENGTH)}…`
    : serialized;
}

export function isChunkLoadError(error: unknown): boolean {
  return CHUNK_ERROR_PATTERN.test(`${errorName(error)}: ${errorMessage(error)}`);
}

export function readErrorLog(): DiagnosticRecord[] {
  try {
    const raw = localStorage.getItem(ERROR_LOG_KEY);
    return raw ? (JSON.parse(raw) as DiagnosticRecord[]) : [];
  } catch {
    // localStorage unavailable or corrupted — start from an empty log
    return [];
  }
}

export function clearErrorLog(): void {
  try {
    localStorage.removeItem(ERROR_LOG_KEY);
  } catch {
    // localStorage unavailable — nothing to clear
  }
}

export function recordError(context: string, error: unknown): void {
  const params = new URLSearchParams(window.location.search);
  const record: DiagnosticRecord = {
    at: new Date().toISOString(),
    context,
    name: errorName(error),
    message: errorMessage(error),
    details: errorDetails(error),
    stack: errorStack(error),
    url: window.location.href,
    looksLikeChunkError: isChunkLoadError(error),
    hasAuthCode: params.has('code'),
    hasAuthError: params.has('error'),
    hasStoredToken: sessionStorage.getItem('access_token') !== null,
  };

  console.error(`[bl312][${context}]`, record, error);

  try {
    const log = readErrorLog();
    log.push(record);
    localStorage.setItem(ERROR_LOG_KEY, JSON.stringify(log.slice(-MAX_RECORDS)));
  } catch {
    // localStorage unavailable — the console.error above remains the record
  }
}
